import { z } from 'zod';
/**
 * Löhne (Modul `payroll`): Einstellungen, Lohnläufe, Lohnzeilen, Erinnerungen und die Kennzahlen je Mitarbeiter. Die Berechnung selbst steht in calc.ts und tax.ts.
 * Wirkia berechnet nach den gesetzlichen Vorgaben, ersetzt aber kein Lohnbüro: Meldungen an Krankenkassen, Finanzamt und Berufsgenossenschaft gehören nicht dazu.
 */
export const PAY_RHYTHMS = ['daily', 'weekly', 'monthly', 'agreed'];
export const PAY_RHYTHM_LABELS = { daily: 'Täglich', weekly: 'Wöchentlich', monthly: 'Monatlich', agreed: 'Nach Vereinbarung' };
export const RUN_PERIODS = ['month', 'week', 'day'];
export const RUN_PERIOD_LABELS = { month: 'Monat', week: 'Woche', day: 'Tag' };
/** Welcher Lohnlauf rechnet welchen Rhythmus ab. „Nach Vereinbarung“ rechnet im Monatslauf mit. */
export const RHYTHM_RUN_PERIOD = { daily: 'day', weekly: 'week', monthly: 'month', agreed: 'month' };
export const RUN_STATUSES = ['draft', 'final', 'paid', 'cancelled'];
export const RUN_STATUS_LABELS = { draft: 'Entwurf', final: 'Festgeschrieben', paid: 'Ausgezahlt', cancelled: 'Storniert' };
/** Kurzer Hinweis, der bei jeder Lohnsteuer und jedem Abzug steht */
export const TAX_NOTICE = 'Nicht verbindliche Berechnung nach dem amtlichen Programmablaufplan. Maßgeblich sind die ELStAM-Daten des Finanzamts und die Abrechnung Ihres Lohnbüros oder Steuerberaters.';
const bpInt = (max) => z.number('Bitte eine Zahl angeben').int('Ganze Zahl').min(0, 'Nicht negativ').max(max);
const nullableText = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null));
export const updatePayrollSettingsInput = z
    .object({
    defaultRhythm: z.enum(PAY_RHYTHMS).optional(),
    /** 0 = Monatsletzter, 1 bis 28 = Tag des Folgemonats */
    payDay: z.number().int().min(0).max(28, 'Höchstens der 28.').optional(),
    reminderDays: z.number().int().min(0).max(30).optional(),
    healthAdditionalBp: bpInt(1000).optional(),
    u1Bp: bpInt(1500).optional(),
    u2Bp: bpInt(500).optional(),
    accidentBp: bpInt(1500).optional(),
    churchRateBp: z.number().int().refine((v) => v === 800 || v === 900, 'Der Satz ist 8 % (Bayern, Baden-Württemberg) oder 9 %').optional(),
    saxony: z.boolean().optional(),
    wageFloorCents: z.number().int().min(0).max(100_000).nullable().optional(),
    companyName: nullableText(70),
    companyIban: nullableText(40),
    companyBic: nullableText(11),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
const day = z.iso.date('Bitte ein Datum angeben');
export const createPayrollRunInput = z
    .object({
    period: z.enum(RUN_PERIODS),
    periodStart: day,
    periodEnd: day,
    payDate: day,
    note: nullableText(500),
    /** Nur diese Mitarbeiter abrechnen. Ohne Angabe: alle aktiven mit dem Rhythmus des Laufs. */
    employeeIds: z.array(z.uuid()).max(500).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    const start = Date.parse(v.periodStart);
    const end = Date.parse(v.periodEnd);
    const days = Math.round((end - start) / 86_400_000) + 1;
    if (!(days >= 1))
        ctx.addIssue({ code: 'custom', path: ['periodEnd'], message: 'Das Ende liegt vor dem Beginn' });
    else if (v.period === 'day' && days !== 1)
        ctx.addIssue({ code: 'custom', path: ['periodEnd'], message: 'Ein Tageslauf umfasst genau einen Tag' });
    else if (v.period === 'week' && days !== 7)
        ctx.addIssue({ code: 'custom', path: ['periodEnd'], message: 'Ein Wochenlauf umfasst genau sieben Tage' });
    else if (v.period === 'month') {
        const lastDay = new Date(Date.UTC(Number(v.periodStart.slice(0, 4)), Number(v.periodStart.slice(5, 7)), 0)).getUTCDate();
        if (v.periodStart.slice(8) !== '01' || v.periodEnd !== `${v.periodStart.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`)
            ctx.addIssue({ code: 'custom', path: ['periodEnd'], message: 'Ein Monatslauf umfasst einen vollen Kalendermonat' });
    }
    if (v.payDate < v.periodStart)
        ctx.addIssue({ code: 'custom', path: ['payDate'], message: 'Die Auszahlung liegt vor dem Beginn des Zeitraums' });
});
const hours = z.number('Bitte eine Zahl angeben').min(0).max(744);
const money = z.number('Bitte einen Betrag angeben').int('Betrag in Cent').min(0).max(100_000_000);
export const updatePayrollLineInput = z
    .object({
    hours: hours.optional(),
    nightHours: hours.optional(),
    sundayHours: hours.optional(),
    holidayHours: hours.optional(),
    bonusCents: money.optional(),
    additionsCents: money.optional(),
    deductionsCents: money.optional(),
    note: nullableText(500),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const cancelPayrollRunInput = z.object({ reason: z.string().trim().min(5, 'Bitte einen Grund angeben (mindestens 5 Zeichen)').max(500) }).strict();
/** Spätester Auszahlungstermin nach § 2 Absatz 1 Nr. 2 MiLoG: letzter Bankarbeitstag des Monats, der auf den Monat der Arbeit folgt. Feiertage kennt die Rechnung nicht, nur Wochenenden. */
export function latestMinimumWagePayDate(periodEnd) {
    const y = Number(periodEnd.slice(0, 4));
    const m = Number(periodEnd.slice(5, 7));
    const d = new Date(Date.UTC(y, m + 1, 0));
    while (d.getUTCDay() === 0 || d.getUTCDay() === 6)
        d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
}
/** Auszahlungstermin nach der Einstellung: 0 = Monatsletzter des Zeitraums, sonst der Tag des Folgemonats. Auf einen Werktag davor gelegt. */
export function defaultPayDate(period, periodStart, periodEnd, payDay) {
    if (period !== 'month')
        return periodEnd;
    const y = Number(periodEnd.slice(0, 4));
    const m = Number(periodEnd.slice(5, 7));
    const d = payDay === 0 ? new Date(Date.UTC(y, m, 0)) : new Date(Date.UTC(y, m, payDay));
    while (d.getUTCDay() === 0 || d.getUTCDay() === 6)
        d.setUTCDate(d.getUTCDate() - 1);
    void periodStart;
    return d.toISOString().slice(0, 10);
}
/** Widerspruch zwischen Beschäftigungsart und Zahlungsrhythmus. Auszubildende bekommen ihre Vergütung spätestens am letzten Arbeitstag des Monats (§ 18 BBiG): „Nach Vereinbarung“ geht dort nicht. */
export function rhythmProblem(kind, rhythm) {
    if (rhythm === 'agreed' && kind === 'apprentice')
        return 'Auszubildende werden spätestens am letzten Arbeitstag des Monats bezahlt (§ 18 Berufsbildungsgesetz). Eine abweichende Vereinbarung ist nicht zulässig.';
    return null;
}
export const PAYROLL_EXPORT_NOTE = 'Die Datei enthält eine SEPA-Sammelüberweisung (pain.001). Laden Sie sie im Online-Banking hoch und geben Sie sie dort frei. Wirkia bucht selbst nichts ab.';
//# sourceMappingURL=payroll.js.map